import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';

import WebMap from '@arcgis/core/WebMap';
import MapView from '@arcgis/core/views/MapView';
import LayerList from '@arcgis/core/widgets/LayerList';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Sublayer from '@arcgis/core/layers/support/Sublayer';
import Graphic from '@arcgis/core/Graphic';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Sketch from '@arcgis/core/widgets/Sketch';
import SketchViewModel from '@arcgis/core/widgets/Sketch/SketchViewModel';
import TimeSlider from '@arcgis/core/widgets/TimeSlider';
import { AssetsService } from '../_services/assets.service';
import { MatDialog } from '@angular/material/dialog';
import TimeExtent from '@arcgis/core/TimeExtent';
import { BehaviorSubject } from 'rxjs';
import { time } from 'console';
import { ExportManagerService } from '../_services/export-manager.service';
import { ExportInjectionPressureManagerService } from '../_services/export-injection-pressure-manager.service';
import { ExportRequest } from '../_models/export-request';
import { MessageDialogComponent } from '../message-dialog/message-dialog.component';
import { BrowseWellDetailsComponent } from '../browse-well-details/browse-well-details.component';
import { BrowseMapImageServiceLayerService } from '../_services/browse-map-image-service-layer.service';
import { WelcomeDialogComponent } from '../welcome-dialog/welcome-dialog.component';
import { BrowseCardComponent } from '../browse-card/browse-card.component';
import { ExportCountResult } from '../_models/export-count-result';

import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";

import { MatDrawer } from "@angular/material/sidenav";

@Component({
  selector: 'app-browse-view',
  templateUrl: './browse-view.component.html',
  styleUrls: ['./browse-view.component.css']
})
export class BrowseViewComponent implements OnInit {

  protected _basemap: string = "topo";

  protected _center = [-97.5, 31.4];

  protected _zoom: number = 7;

  public aoiGraphicSubject = new BehaviorSubject<Graphic>(null);

  public aoiSelectedWellsResult = new BehaviorSubject<any>(null);
  public aoiInjectionPressureWellsResult = new BehaviorSubject<any>(null);

  public exportRecordCount = new BehaviorSubject<number>(0);
  public exportInjectionPressureRecordCount = new BehaviorSubject<number>(0);

  protected INJECTION_VOLUME_WELLS_LAYER_ID = 0;
  protected INJECTION_PRESSURE_WELLS_LAYER_ID = 2;


  protected _fullTimeExtent: TimeExtent = new TimeExtent({
    start: new Date(2015, 0, 1, 0, 0, 0, 0),
    end: new Date()
  });

  protected _timeExtent: TimeExtent = new TimeExtent({
    start: new Date(2015, 0, 1, 0, 0, 0, 0),
    end: new Date()
  });

  _map: WebMap;
  _mapView: MapView;
  _imageLayer: MapImageLayer;
  _layerList: LayerList = null;
  _sketch: Sketch = null;
  _timeSlider: TimeSlider = null;
  _aoiSketch: SketchViewModel;
  _injectionVolumeWellsLayer: Sublayer = null;
  _injectionPressureWellsLayer: Sublayer = null;

  _aoiSketchGraphicsLayer: GraphicsLayer = null;

  _fetchAoiDataTimeout = null;
  _fetchAoiStatsTimeout = null;

  @ViewChild('mapViewNode', {}) protected mapViewEl: ElementRef;

  @ViewChild('legendSidenav', {}) protected legendSidenav: MatDrawer;

  public get fullBeginTime(): Date {
    return this._fullTimeExtent.start;
  }

  public set fullBeginTime(newVal: Date) {
    this._fullTimeExtent.start = newVal;
  }

  public get fullEndTime(): Date {
    return this._fullTimeExtent.end;
  }

  public set fullEndTime(newVal: Date) {
    this._fullTimeExtent.end;
  }

  public get beginTime(): Date {
    return this._timeExtent.start;
  }

  public set beginTime(newVal: Date) {
    this._timeExtent.start = newVal;
  }

  public get endTime(): Date {
    return this._timeExtent.end;
  }

  public set endTime(newVal: Date) {
    this._timeExtent.end = newVal;
  }

  constructor(
    public assetsService: AssetsService,
    public exportService: ExportManagerService,
    public exportInjectionPressureManagerService: ExportInjectionPressureManagerService,
    public matDialog: MatDialog,
    public browseMapImageServiceLayerService: BrowseMapImageServiceLayerService
  ) {
    
  }

  ngOnInit(): void {

  }

  ngAfterViewInit(): void {
    // Set type for Map constructor properties
    const mapProperties = {
      basemap: this._basemap
    };

    this._map = new WebMap(mapProperties);

    // Set type for MapView constructor properties
    const mapViewProperties = {
      container: this.mapViewEl.nativeElement,
      center: this._center,
      zoom: this._zoom,
      map: this._map
    };

    this._mapView = new MapView(mapViewProperties);

    //Create the layer list
    this._layerList = new LayerList({
      view: this._mapView,
      listItemCreatedFunction: function (event) {

        //This section forces the layer list to behave like a legend, with the swatches and so forth...
        const item = event.item;

        if (item.layer.type != undefined && item.layer.type != "group" && item.layer.type != "graphics") {
          item.panel = {
            content: "legend",
            open: true
          }
        }
      },
      container: "legend-container"
    });

    reactiveUtils.on(() => this._mapView.popup, "trigger-action", (event) => {
      var feature = this._mapView.popup.selectedFeature;

      if (event.action.id == "well-details") {
        //Launch the well details.
        var graphic = this._mapView.popup.selectedFeature;
        this.launchWellDetails(graphic);
      }

      if (event.action.id == "export-data") {
        var graphic = this._mapView.popup.selectedFeature;
        this.exportDataForWell(graphic);
      }

      if (event.action.id == "export-pressure-data") {
        var graphic = this._mapView.popup.selectedFeature;
        this.exportPressureDataForWell(graphic);
      }
    });

    //Create the AOI Sketch, for later.
    this.initAOIDrawing();

    //Initialize the time slider
    this.initTimeSlider();

    this.initBasinsLayer();

    //Add the SRAs group layer.  The layers
    this.initSIRsGroupLayer();

    //Add the SRAs group layer.  The layers
    this.initSRAsGroupLayer();

    //Set up the stations layer
    this.initStationsLayer();

    //Finally, set up our image layer, which is the data source
    this.initMapImageLayer();

    //Launch the welcome dialog
    this.launchWelcomeDialog();
  }

  protected initTimeSlider() {

    var stopsInterval: any = {
      value: 1,
      unit: "months"
    };

    //Set up the time slider
    this._timeSlider = new TimeSlider({
      container: "timeSlider",
      view: this._mapView,
      fullTimeExtent: this._fullTimeExtent,
      timeExtent: this._timeExtent,
      stops: stopsInterval
    });

    var _this = this;

    this._timeSlider.watch("timeExtent", function () {

      var teSlider = _this._timeSlider.timeExtent;
      _this.beginTime = teSlider.start;
      _this.endTime = teSlider.end;

      //Reload the AOI data
      _this.fetchAoiData();
    });
  }

  protected initMapImageLayer() {

    this.browseMapImageServiceLayerService.currentMapImageService.subscribe((imgLyr) => {
      if(imgLyr != null) {
        if (this._imageLayer != null) {
          //If our instance handle on the image layer isn't null, we need to remove the old one before we start using this new one...
          this._map.remove(this._imageLayer);
        }

        //Save the handle on the image layer.
        this._imageLayer = imgLyr;


        this._imageLayer.when((imageLayer) => {
          if (imageLayer.timeInfo != null && imageLayer.timeInfo.fullTimeExtent != null) {
            var fullTimeExtent = imageLayer.timeInfo.fullTimeExtent;

            fullTimeExtent.start = new Date(2016, 0, 1);
            fullTimeExtent.end = new Date();

            this._fullTimeExtent.start = fullTimeExtent.start;
            this._fullTimeExtent.end = fullTimeExtent.end;

            this._timeExtent.start = fullTimeExtent.start;
            this._timeExtent.end = fullTimeExtent.end;
          }
        });

        this._imageLayer.when((layer: MapImageLayer) => {
          this.initPopupTemplates(layer);

          //Get a handle on the wells layer
          this._injectionVolumeWellsLayer = layer.allSublayers.find((sl: Sublayer) => {
            var ret = false;
            if (sl.id == this.INJECTION_VOLUME_WELLS_LAYER_ID) {
              ret = true;
            }

            return ret;
          });

          //Get a handle on the wells layer
          this._injectionPressureWellsLayer = layer.allSublayers.find((sl: Sublayer) => {
            var ret = false;
            if (sl.id == this.INJECTION_PRESSURE_WELLS_LAYER_ID) {
              ret = true;
            }

            return ret;
          });

          //Fetch the AOI data, now that we should have a layer for it.
          this.fetchAoiData()

        });

        this._map.add(this._imageLayer);
      }
    });
  }

  protected initStationsLayer() {
    var fl = new FeatureLayer({
      url: "https://maps.texnet.beg.utexas.edu/arcgis/rest/services/catalog/TexNetCatalog/FeatureServer/2",
      visible: false
    });

    this._map.add(fl, );
  }

  protected initSIRsGroupLayer() {
    var fl = new MapImageLayer({
      url: "https://maps.texnet.beg.utexas.edu/arcgis/rest/services/sra/sir/MapServer",
      title: "Seismic Investigation Regions",
      visible: true
    });

    var ind = this._map.layers.length - 1;

    this._map.add(fl, ind);
  }

  protected initSRAsGroupLayer() {
    var fl = new MapImageLayer({
      url: "https://maps.texnet.beg.utexas.edu/arcgis/rest/services/sra/sra/MapServer",
      title: "Seismic Response Areas",
      visible: true
    });

    var ind = this._map.layers.length - 1;

    this._map.add(fl, ind);
  }

  protected initBasinsLayer() {
    var fl = new MapImageLayer({
      url: "https://maps.texnet.beg.utexas.edu/arcgis/rest/services/BasinsAndPlays/MapServer",
      title: "Texas Basins and Plays",
      visible: false
    });

    var ind = this._map.layers.length - 1;

    this._map.add(fl, ind);
  }

  protected initPopupTemplates(layer: MapImageLayer) {
    if (layer != null) {
      layer.sublayers.forEach((sublayer: Sublayer) => {

        if (sublayer.id == this.INJECTION_VOLUME_WELLS_LAYER_ID) {
          this.assetsService.getAssetAsJson("assets/PopupTemplates/disposalwells.json").then((result) => {
            sublayer.popupTemplate = result;
          },
            (error) => {
              var q = 0;
            });
        }
        else if(sublayer.id == this.INJECTION_PRESSURE_WELLS_LAYER_ID) {
          this.assetsService.getAssetAsJson("assets/PopupTemplates/injectionpressurewells.json").then((result) => {
            sublayer.popupTemplate = result;
          },
            (error) => {
              var q = 0;
            });
        }

      });
    }
  }

  protected initAOIDrawing() {
    this._aoiSketchGraphicsLayer = new GraphicsLayer({
      listMode: "hide"
    });


    var polygonSymbol: any = {
      type: "simple-fill",  // autocasts as new SimpleMarkerSymbol()
      color: "rgba(200,0,0, 0.25)",
      style: "solid",
      outline: {
        color: "red",
        width: 2
      }
    };

    this._aoiSketch = new SketchViewModel({
      view: this._mapView,
      layer: this._aoiSketchGraphicsLayer,
      polygonSymbol: polygonSymbol,
      updateOnGraphicClick: false
    });

    this._map.add(this._aoiSketchGraphicsLayer);

    this._aoiSketch.on("create", (event) => {
      if (event.state === "complete") {
        this.handleAOIDrawComplete(event);
      }
    });

    //Listen to the AOI graphic subject and call 
    this.aoiGraphicSubject.subscribe((newAoi) => {
      if (this._aoiSketchGraphicsLayer != null) {

        //Remove any previously existing graphics.
        this._aoiSketchGraphicsLayer.removeAll();

        if (newAoi != null) {

          this._aoiSketchGraphicsLayer.add(newAoi);
        }

        this.fetchAoiData();
      }
    });
  }

  protected handleAOIDrawComplete(evt: any) {
    var poly = evt.graphic;

    this.setAOI(poly);
  }

  protected setAOI(poly: Graphic) {
    this.aoiGraphicSubject.next(poly);
  }

  public doAOISketch() {

    this.clearAOI();

    this._aoiSketch.create("polygon");
  }

  public clearAOI() {

    this.aoiGraphicSubject.next(null);
  }

  protected launchWellDetails(well: Graphic) {
    this.matDialog.open(BrowseWellDetailsComponent, {
      data: {
        well: well,
        wellsLayer: this._injectionVolumeWellsLayer,
        timeExtent: this._timeExtent
      }
    });
  }

  protected fetchAoiData() {

    //If we had a timeout already set, abort.
    if (this._fetchAoiDataTimeout != null) {
      clearTimeout(this._fetchAoiDataTimeout);
      this._fetchAoiDataTimeout = null;
    }

    this._fetchAoiDataTimeout = setTimeout(() => {

      if (this._injectionVolumeWellsLayer != null) {
        var query = this._injectionVolumeWellsLayer.createQuery();

        if (this.aoiGraphicSubject.value != null) {
          query.geometry = this.aoiGraphicSubject.value.geometry;
        }

        query.timeExtent = this._timeExtent;
        query.outFields = ["Id"];

        //Add a where clause to reflect any additional parameters.

        //Run the query...
        this._injectionVolumeWellsLayer.queryFeatures(query).then((result) => {

          //Set the selected well set to our result.
          this.aoiSelectedWellsResult.next(result);

          this.fetchAoiStatistics();
        },
        (err) => {

        });
      }

      if (this._injectionPressureWellsLayer != null) {
        var query = this._injectionPressureWellsLayer.createQuery();

        if (this.aoiGraphicSubject.value != null) {
          query.geometry = this.aoiGraphicSubject.value.geometry;
        }

        query.timeExtent = this._timeExtent;
        query.outFields = ["Id"];

        //Add a where clause to reflect any additional parameters.

        //Run the query...
        this._injectionPressureWellsLayer.queryFeatures(query).then((result) => {

          //Set the selected well set to our result.
          this.aoiInjectionPressureWellsResult.next(result);

          this.fetchAoiStatistics();
        },
        (err) => {

        });
      }

    }, 750);

  }

  protected fetchAoiStatistics() {

    var injVolWellIds = this.getSelectedWellIds();

    var erInjectionVolume: ExportRequest = this.getExportRequest(injVolWellIds);

    this.exportService.exportCount(erInjectionVolume).then((result: ExportCountResult) => {
      this.exportRecordCount.next(result.Count);
    },
    (err) => {

    });

    var injPressureWellIds = this.getSelectedInjectionPressureWellIds();

    var erInjectionPressure: ExportRequest = this.getExportRequest(injPressureWellIds);

    this.exportInjectionPressureManagerService.exportCount(erInjectionPressure).then((result: ExportCountResult) => {
      this.exportInjectionPressureRecordCount.next(result.Count);
    },
    (err) => {

    });
  }

  protected getExportRequest(wellIds: Array<number>): ExportRequest {

    var req: ExportRequest = new ExportRequest();
    req.IncludeWellIds = false;
    req.WellIds = wellIds;

    var tes: Date = this._timeExtent.start;
    var tee: Date = this._timeExtent.end;

    var beginYear = tes.getFullYear();
    var beginMonth = tes.getMonth() + 1;
    var beginDay = tes.getDate();

    var endYear = tee.getFullYear();
    var endMonth = tee.getMonth() + 1;
    var endDay = tee.getDate();

    req.BeginYear = beginYear;
    req.BeginMonth = beginMonth;
    req.EndYear = endYear;
    req.EndMonth = endMonth;

    //At some point, we should maybe consider making this selectable, particularly if we start offering other formats.
    req.Format = "excel";

    return req;
  }

  protected getSelectedWellIds(): Array<number> {
    var wellIds = [];

    if (this.aoiSelectedWellsResult.value != null) {
      this.aoiSelectedWellsResult.value.features.forEach((feature) => {
        wellIds.push(feature.attributes.Id);
      });
    }

    return wellIds;
  }

  protected getSelectedInjectionPressureWellIds(): Array<number> {
    var wellIds = [];

    if (this.aoiInjectionPressureWellsResult.value != null) {
      this.aoiInjectionPressureWellsResult.value.features.forEach((feature) => {
        wellIds.push(feature.attributes.Id);
      });
    }

    return wellIds;
  }

  public exportDataForWell(wellGraphic: Graphic) {
    var wellids = [wellGraphic.attributes.Id];

    this.exportDataForWellIds(wellids);
  }

  public exportPressureDataForWell(wellGraphic: Graphic) {
    var wellids = [wellGraphic.attributes.Id];

    this.exportPressureDataForWellIds(wellids);
  }

  public exportData() {
    var wellids = this.getSelectedWellIds();
    this.exportDataForWellIds(wellids);
  }

  public exportDataForWellIds(wellIds: Array<number>) {

    var req: ExportRequest = this.getExportRequest(wellIds);

    this.exportService.export(req, true).then((result) => {
      //We don't actually have to do anything here.  
    },
      (err) => {
        //Show an alert dialog, so we don't just leave the user hanging.
        this.matDialog.open(MessageDialogComponent, {
          data: {
            title: "Error",
            message: "Something went wrong while attempting to export your requested data.  Please try the operation again."
          }
        });
      });
  }

  public exportPressureData() {
    var wellids = this.getSelectedInjectionPressureWellIds();
    this.exportPressureDataForWellIds(wellids);
  }

  public exportPressureDataForWellIds(wellIds) {
    var req: ExportRequest = this.getExportRequest(wellIds);

    this.exportInjectionPressureManagerService.export(req, true).then((result) => {
      //We don't actually have to do anything here.  
    },
      (err) => {
        //Show an alert dialog, so we don't just leave the user hanging.
        this.matDialog.open(MessageDialogComponent, {
          data: {
            title: "Error",
            message: "Something went wrong while attempting to export your requested data.  Please try the operation again."
          }
        });
      });
  }

  public launchWelcomeDialog() {
    //Show an alert dialog, so we don't just leave the user hanging.
    this.matDialog.open(WelcomeDialogComponent, {
      disableClose: true
    });
  }

  public readyToExport(): boolean {
    var ret: boolean = false;

    if (this.aoiSelectedWellsResult.value != null &&
      this.aoiSelectedWellsResult.value.features != null &&
      this.aoiSelectedWellsResult.value.features.length > 0) {
      ret = true;
    }

    return ret;
  }
}
